import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  useTheme,
} from "@mui/material";
import settings from "app/settings";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { IMaskMixin } from "react-imask";
import { ColorIndicatorProps } from "../types";
import { getColorsFromString } from "./../../../modules/helpers";
declare global {
  interface Window {
    lastScroll: number;
  }
}

export function getTextFieldColorIndicatorSx(
  id: any,
  { indicatorColor, indicatorOpacity }: ColorIndicatorProps,
) {
  let sx = {};
  if (indicatorColor === "auto") {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${
          getColorsFromString(
            id,
            indicatorOpacity || settings.constants?.autoColorOpacity || 1,
          ).backgroundColor
        }`,
      },
    };
  } else if (indicatorColor) {
    sx = {
      "& .MuiFilledInput-root": {
        boxShadow: `inset 5px 0 0 0 ${indicatorColor}`,
      },
    };
  }
  return sx;
}

export interface TextFieldProps
  extends
    ColorIndicatorProps,
    Omit<MuiTextFieldProps, "error" | "onChange" | "onBlur" | "value"> {
  value?: string | number | null;
  error?: string;
  helperText?: string;
  mask?: any;
  numberOptions?: {
    scale?: number; // digits after point, 0 for integers
    signed?: boolean; // disallow negative
    thousandsSeparator?: string; // any single char
    padFractionalZeros?: boolean; // if true, then pads zeros at end to the length of scale
    normalizeZeros?: boolean; // appends or removes zeros at ends
    radix?: string; // fractional delimiter
    mapToRadix?: string[]; // symbols to process as radix

    // additional number interval options (e.g.)
    min?: number;
    max?: number;
  };
  onChange?: (event: { target: { value?: string | number | null } }) => void;
  onBlur?: Function;
  onEnter?: Function;
  onEscape?: Function;
  onChangeByKeyPress?: Function;
  useDelayedKeystrokes?: boolean;
  unmask?: boolean;
  editable?: boolean;
  isDirty?: boolean;
  required?: boolean;
  clearable?: boolean;
  onClear?: Function;
  alwaysShowClearButton?: boolean;
  keepCursorPosition?: boolean;
}

const MaskedTextField = IMaskMixin((props: any) => <MuiTextField {...props} />);

interface MetadataObj {
  [key: string]: any;
}

export default function TextField({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  onChangeByKeyPress,
  editable,
  useDelayedKeystrokes = true,
  isDirty,
  label: _label,
  required,
  clearable,
  indicatorColor,
  indicatorOpacity,
  onEnter,
  alwaysShowClearButton,
  onClear,
  onEscape,
  numberOptions,
  keepCursorPosition,
  ...textFieldProps
}: TextFieldProps) {
  const [innerValue, setInnerValue] = useState<any>(value);
  const timer = useRef<any>();
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<{
    start: number;
    end: number;
    originalValue: string;
    userInput?: boolean;
  } | null>(null);
  const isUserInputRef = useRef<boolean>(false);

  const getValue = () => {
    return innerValue === undefined ? null : innerValue;
  };

  useEffect(() => {
    // Store cursor position if the change is coming from outside (not user input)
    if (
      !isUserInputRef.current &&
      inputRef.current &&
      document.activeElement === inputRef.current
    ) {
      cursorPositionRef.current = {
        start: inputRef.current.selectionStart || 0,
        end: inputRef.current.selectionEnd || 0,
        originalValue: String(innerValue || ""),
        userInput: false,
      };
    }

    const previousValue = String(innerValue || "");
    setInnerValue(value);
    const newValue = String(value || "");

    // Restore cursor position after value update
    if (
      cursorPositionRef.current &&
      inputRef.current &&
      document.activeElement === inputRef.current &&
      keepCursorPosition
    ) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        if (inputRef.current && cursorPositionRef.current) {
          let newCursorPosition = cursorPositionRef.current.start;

          // If this was a user input that got transformed, try to be smart about cursor positioning
          if (cursorPositionRef.current.userInput) {
            newCursorPosition = calculateCursorPosition(
              cursorPositionRef.current.originalValue,
              newValue,
              cursorPositionRef.current.start,
            );
          } else {
            // For external changes, just clamp to new length
            newCursorPosition = Math.min(
              cursorPositionRef.current.start,
              newValue.length,
            );
          }

          inputRef.current.setSelectionRange(
            newCursorPosition,
            newCursorPosition,
          );
          cursorPositionRef.current = null;
        }
      }, 0);
    }

    // Reset the flag
    isUserInputRef.current = false;
  }, [value, keepCursorPosition]);

  // Smart cursor position calculation for transformed text
  function calculateCursorPosition(
    originalValue: string,
    transformedValue: string,
    originalCursor: number,
  ): number {
    // If lengths are the same, cursor position should be the same
    if (originalValue.length === transformedValue.length) {
      return Math.min(originalCursor, transformedValue.length);
    }

    // Find the position by comparing character by character up to cursor
    let newPosition = originalCursor;
    let originalIndex = 0;
    let transformedIndex = 0;

    while (
      originalIndex < originalCursor &&
      transformedIndex < transformedValue.length
    ) {
      const originalChar = originalValue[originalIndex];
      const transformedChar = transformedValue[transformedIndex];

      if (originalChar === transformedChar) {
        // Characters match, advance both
        originalIndex++;
        transformedIndex++;
      } else if (originalChar.toLowerCase() === transformedChar.toLowerCase()) {
        // Same character but different case (capitalization)
        originalIndex++;
        transformedIndex++;
      } else {
        // Characters don't match, try to find where they sync up again
        transformedIndex++;
      }
    }

    return Math.min(transformedIndex, transformedValue.length);
  }

  /**
   * Scroll to first error field
   */
  useEffect(() => {
    const t = new Date().getTime();

    if (!window.lastScroll) {
      window.lastScroll = 0;
    }
    if (error && fieldRef.current && window.lastScroll < t - 1000) {
      window.lastScroll = t;
      var headerOffset = 100;
      var elementPosition =
        fieldRef.current && fieldRef.current.getBoundingClientRect
          ? fieldRef.current.getBoundingClientRect().top
          : 0;
      var offsetPosition = elementPosition - headerOffset;

      /*window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });*/
    }
  }, [error]);

  const handleChange = (evt: any) => {
    // Mark that this change is from user input
    isUserInputRef.current = true;

    // Store cursor position before any transformations
    const inputElement = evt.target;
    const cursorStart = inputElement.selectionStart;
    const cursorEnd = inputElement.selectionEnd;
    const originalValue = String(inputElement.value || "");

    //let evt = { target: { value: _evt?.target?.value || null } };
    if (evt.target.value === "") {
      evt.target.value = null;
    }

    if (
      evt.target.value &&
      (textFieldProps.type === "number" || textFieldProps.mask === Number)
    ) {
      ///evt.persist && evt.persist();
      evt = { target: { value: Number(evt.target.value) } };
    }

    if (evt.target.value === getValue()) {
      return;
    }

    setInnerValue(evt.target.value);
    onChangeByKeyPress && onChangeByKeyPress(evt);

    // Store cursor position info for potential restoration after external transform
    cursorPositionRef.current = {
      start: cursorStart,
      end: cursorEnd,
      originalValue: originalValue,
      userInput: true,
    };

    if (useDelayedKeystrokes) {
      evt.persist && evt.persist();
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
      timer.current = setTimeout(() => {
        onChange && onChange(evt);
      }, 800);
    } else {
      onChange && onChange(evt);
    }
  };

  const handleAccept = (unmaskedValue: any) => {
    // Mark that this change is from user input
    isUserInputRef.current = true;

    if (
      unmaskedValue &&
      (textFieldProps.type === "number" || textFieldProps.mask === Number)
    ) {
      handleChange({ target: { value: Number(unmaskedValue) } });
    } else {
      handleChange({ target: { value: unmaskedValue } });
    }
  };

  const handleBlur = (evt: any) => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
    if (textFieldProps.unmask) {
      const e = { target: { value: innerValue } };
      if (String(value) !== String(innerValue)) {
        onChange && onChange(e);
      }
      onBlur && onBlur(e);
    } else {
      if (evt.target.value === "") {
        evt.target.value = null;
      }

      if (String(evt.target.value) !== String(value)) {
        if (
          evt.target.value &&
          (textFieldProps.type === "number" || textFieldProps.mask === Number)
        ) {
          evt = { target: { value: Number(evt.target.value) } };
        }
        onChange && onChange(evt);
        return;
      }

      onBlur && onBlur(evt);
    }
  };

  const Input = textFieldProps.mask ? MaskedTextField : MuiTextField;
  let MaskedProps: MetadataObj = {};
  if (textFieldProps.mask === Number) {
    MaskedProps = { ...MaskedProps, ...numberOptions };
  }
  if (textFieldProps.unmask) {
    MaskedProps.onAccept = handleAccept;
  }

  const label = required ? (
    <span>
      {_label} <span style={{ color: "red" }}>*</span>
    </span>
  ) : (
    _label
  );
  const theme = useTheme();

  function handleOnEnter(evt: KeyboardEvent<HTMLInputElement>) {
    if (evt.key === "Enter") {
      onEnter && onEnter(evt);
    }
    if (evt.key === "Escape") {
      onEscape && onEscape(evt);
    }
  }

  return (
    <Input
      //@ts-ignore
      ref={fieldRef}
      inputRef={inputRef}
      label={textFieldProps.hiddenLabel ? undefined : label}
      value={
        !!innerValue || innerValue === 0
          ? textFieldProps.mask
            ? String(innerValue)
            : innerValue
          : ""
      }
      onBlur={handleBlur}
      color={isDirty ? "secondary" : "primary"}
      onChange={!textFieldProps.unmask ? handleChange : undefined}
      error={error ? true : false}
      helperText={error || helperText}
      onKeyDown={onEnter ? handleOnEnter : undefined}
      sx={{
        ...getTextFieldColorIndicatorSx(textFieldProps.id, {
          indicatorColor,
          indicatorOpacity,
        }),
        ...textFieldProps?.sx,
      }}
      inputProps={{
        autoCorrect: "off",
        autoCapitalize: "none",
      }}
      {...MaskedProps}
      {...textFieldProps}
      InputLabelProps={{
        shrink: value === 0 || !!value ? true : undefined,
        ...textFieldProps.InputLabelProps,
      }}
      InputProps={
        clearable
          ? {
              ...textFieldProps.InputProps,
              endAdornment: (
                <InputAdornment
                  position="end"
                  style={{ marginRight: textFieldProps.multiline ? 0 : -6 }}
                >
                  {(!!value || alwaysShowClearButton) && (
                    <IconButton
                      onClick={() => {
                        handleChange({ target: { value: null } });
                        onClear && onClear();
                      }}
                      sx={{ color: theme.palette.text.primary }}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  {textFieldProps.InputProps?.endAdornment}
                </InputAdornment>
              ),
            }
          : textFieldProps.InputProps
      }
    />
  );
}
